import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft, 
  Save, 
  Send, 
  Upload, 
  Image as ImageIcon, 
  FileText, 
  MessageSquare, 
  CheckSquare,
  FileCheck,
  Clock,
  User,
  Car,
  MapPin,
  Calendar
} from 'lucide-react';
import { faelleAPI } from '../utils/api';
import { toast } from 'sonner';
const FallDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();  const [fall, setFall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('uebersicht');
  const [medien, setMedien] = useState([]);
  const [dokumente, setDokumente] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [todos, setTodos] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [newTodo, setNewTodo] = useState('');

  useEffect(() => {
    fetchFall();
    fetchMedien();
    fetchDokumente();
    fetchChat();
    fetchTodos();
  }, [id]);

  const fetchFall = async () => {
    try {
      setLoading(true);
      const data = await faelleAPI.getById(id);
      setFall(data);
    } catch (err) {
      console.error('Failed to fetch fall:', err);
      toast.error('Fehler', {
        description: 'Fall konnte nicht geladen werden'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchMedien = async () => {
    try {
      const data = await faelleAPI.getDateien(id);
      setMedien(data.filter(d => d.typ === 'bild'));
    } catch (err) {
      console.error('Failed to fetch medien:', err);
    }
  };

  const fetchDokumente = async () => {
    try {
      const data = await faelleAPI.getDateien(id);
      setDokumente(data.filter(d => d.typ === 'dokument'));
    } catch (err) {
      console.error('Failed to fetch dokumente:', err);
    }
  };

  const fetchChat = async () => {
    try {
      const data = await faelleAPI.getMessages(id);
      setChatMessages(data);
    } catch (err) {
      console.error('Failed to fetch chat:', err);
    }
  };

  const fetchTodos = async () => {
    try {
      const data = await faelleAPI.getTodos(id);
      setTodos(data);
    } catch (err) {
      console.error('Failed to fetch todos:', err);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    
    try {
      await faelleAPI.sendMessage(id, newMessage);
      setNewMessage('');
      fetchChat();
      toast.success('Erfolgreich', {
        description: 'Nachricht gesendet'
      });
    } catch (err) {
      toast.error('Fehler', {
        description: 'Nachricht konnte nicht gesendet werden'
      });
    }
  };

  const handleAddTodo = async () => {
    if (!newTodo.trim()) return;
    
    try {
      await faelleAPI.createTodo(id, {
        titel: newTodo,
        beschreibung: '',
        fall_id: id
      });
      setNewTodo('');
      fetchTodos();
      toast.success('Erfolgreich', {
        description: 'Todo hinzugefügt'
      });
    } catch (err) {
      toast.error('Fehler', {
        description: 'Todo konnte nicht hinzugefügt werden'
      });
    }
  };

  const handleToggleTodo = async (todoId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'erledigt' ? 'offen' : 'erledigt';
      await faelleAPI.updateTodo(todoId, { status: newStatus });
      fetchTodos();
    } catch (err) {
      toast.error('Fehler', {
        description: 'Status konnte nicht geändert werden'
      });
    }
  };

  const handleFileUpload = async (event, typ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }
    formData.append('typ', typ);

    try {
      await faelleAPI.uploadDatei(id, formData);
      if (typ === 'bild') {
        fetchMedien();
      } else {
        fetchDokumente();
      }
      toast.success('Erfolgreich', {
        description: 'Datei hochgeladen'
      });
    } catch (err) {
      toast.error('Fehler', {
        description: 'Datei konnte nicht hochgeladen werden'
      });
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      neu: 'bg-blue-100 text-blue-700',
      in_bearbeitung: 'bg-blue-100 text-blue-700',
      abgeschlossen: 'bg-green-100 text-green-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!fall) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-gray-500">Fall nicht gefunden</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/gutachten')}
              className="hover:bg-gray-100"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zurück
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{fall.aktenzeichen}</h1>
              <p className="text-gray-500">Fall-Details</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Badge className={getStatusColor(fall.status)}>
              {fall.status}
            </Badge>
            <Button className="bg-gradient-to-r from-blue-500 to-blue-700 text-white">
              <Save className="w-4 h-4 mr-2" />
              Speichern
            </Button>
          </div>
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-3">
                <Car className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-xs text-gray-500">Kennzeichen</p>
                  <p className="font-semibold">{fall.kennzeichen}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-3">
                <Calendar className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-xs text-gray-500">Unfall-Datum</p>
                  <p className="font-semibold">{fall.unfall_datum || 'N/A'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-3">
                <MapPin className="w-5 h-5 text-red-600" />
                <div>
                  <p className="text-xs text-gray-500">Unfall-Ort</p>
                  <p className="font-semibold">{fall.unfall_ort || 'N/A'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-3">
                <User className="w-5 h-5 text-green-600" />
                <div>
                  <p className="text-xs text-gray-500">Auftraggeber</p>
                  <p className="font-semibold">{fall.auftraggeber || 'N/A'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-6 bg-gray-100">
            <TabsTrigger value="uebersicht">Übersicht</TabsTrigger>
            <TabsTrigger value="medien">Medien</TabsTrigger>
            <TabsTrigger value="dokumente">Dokumente</TabsTrigger>
            <TabsTrigger value="chat">Chat</TabsTrigger>
            <TabsTrigger value="todos">Todos</TabsTrigger>
            <TabsTrigger value="gutachten">Gutachten</TabsTrigger>
          </TabsList>

          {/* Übersicht Tab */}
          <TabsContent value="uebersicht" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Fall-Informationen</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Aktenzeichen</label>
                    <p className="text-gray-900">{fall.aktenzeichen}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">VIN</label>
                    <p className="text-gray-900">{fall.vin || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Versicherer</label>
                    <p className="text-gray-900">{fall.versicherer || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Ansprechpartner</label>
                    <p className="text-gray-900">{fall.ansprechpartner || 'N/A'}</p>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Bemerkung</label>
                  <Textarea 
                    value={fall.bemerkung || ''} 
                    rows={4}
                    className="mt-1"
                    readOnly
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Medien Tab */}
          <TabsContent value="medien" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Bilder & Medien</CardTitle>
                  <Button variant="outline" className="relative">
                    <Upload className="w-4 h-4 mr-2" />
                    Hochladen
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      className="absolute inset-0 opacity-0 cursor-pointer"
                      onChange={(e) => handleFileUpload(e, 'bild')}
                    />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {medien.length === 0 ? (
                  <div className="text-center py-12">
                    <ImageIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">Noch keine Bilder hochgeladen</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {medien.map((media) => (
                      <div key={media.id} className="relative group">
                        <img 
                          src={media.url} 
                          alt={media.name}
                          className="w-full h-40 object-cover rounded-lg"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all rounded-lg flex items-center justify-center">
                          <Button 
                            variant="ghost" 
                            className="opacity-0 group-hover:opacity-100 text-white"
                          >
                            Ansehen
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Dokumente Tab */}
          <TabsContent value="dokumente" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Dokumente</CardTitle>
                  <Button variant="outline" className="relative">
                    <Upload className="w-4 h-4 mr-2" />
                    Hochladen
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.xls,.xlsx"
                      className="absolute inset-0 opacity-0 cursor-pointer"
                      onChange={(e) => handleFileUpload(e, 'dokument')}
                    />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {dokumente.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">Noch keine Dokumente hochgeladen</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {dokumente.map((doc) => (
                      <div 
                        key={doc.id} 
                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                      >
                        <div className="flex items-center space-x-3">
                          <FileText className="w-5 h-5 text-blue-600" />
                          <div>
                            <p className="font-medium">{doc.name}</p>
                            <p className="text-xs text-gray-500">{doc.size}</p>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm">
                          Download
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Chat Tab */}
          <TabsContent value="chat" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Chat mit Backoffice</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Chat Messages */}
                  <div className="h-96 overflow-y-auto space-y-3 p-4 bg-gray-50 rounded-lg">
                    {chatMessages.length === 0 ? (
                      <div className="text-center py-12">
                        <MessageSquare className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                        <p className="text-gray-500">Noch keine Nachrichten</p>
                      </div>
                    ) : (
                      chatMessages.map((msg) => {
                        const currentUserId = JSON.parse(localStorage.getItem('autogutachter24_user') || '{}').user_id;
                        const isOwnMessage = msg.absender_id === currentUserId;
                        
                        return (
                          <div 
                            key={msg.chat_id}
                            className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                          >
                            <div className={`max-w-sm p-3 rounded-lg ${
                              isOwnMessage 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-900 border'
                            }`}>
                              {!isOwnMessage && (
                                <p className="text-xs font-semibold mb-1 opacity-70">{msg.absender_name}</p>
                              )}
                              <p className="text-sm">{msg.nachricht}</p>
                              <p className="text-xs mt-1 opacity-70">
                                {new Date(msg.created_at).toLocaleString('de-DE')}
                              </p>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* Message Input */}
                  <div className="flex space-x-2">
                    <Input
                      placeholder="Nachricht eingeben..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    />
                    <Button 
                      onClick={handleSendMessage}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Todos Tab */}
          <TabsContent value="todos" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Aufgaben</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Add Todo */}
                  <div className="flex space-x-2">
                    <Input
                      placeholder="Neue Aufgabe hinzufügen..."
                      value={newTodo}
                      onChange={(e) => setNewTodo(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddTodo()}
                    />
                    <Button onClick={handleAddTodo}>
                      Hinzufügen
                    </Button>
                  </div>

                  {/* Todo List */}
                  <div className="space-y-2">
                    {todos.length === 0 ? (
                      <div className="text-center py-12">
                        <CheckSquare className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                        <p className="text-gray-500">Noch keine Aufgaben</p>
                      </div>
                    ) : (
                      todos.map((todo) => (
                        <div 
                          key={todo.todo_id}
                          className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50"
                        >
                          <input
                            type="checkbox"
                            checked={todo.status === 'erledigt'}
                            onChange={() => handleToggleTodo(todo.todo_id, todo.status)}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                          <span className={`flex-1 ${todo.status === 'erledigt' ? 'line-through text-gray-500' : ''}`}>
                            {todo.titel}
                          </span>
                          {todo.status === 'erledigt' && (
                            <Badge variant="success" className="bg-green-100 text-green-700">
                              Erledigt
                            </Badge>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Gutachten Tab */}
          <TabsContent value="gutachten" className="space-y-4">
            {/* Gutachten Vorschau - Druckbare Ansicht */}
            <div className="bg-white shadow-lg rounded-lg overflow-hidden">
              {/* Header mit Logo und Titel */}
              <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-8 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {/* Logo Placeholder */}
                    <div className="bg-white rounded-lg p-4 shadow-lg">
                      <svg className="w-16 h-16 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div>
                      <h1 className="text-3xl font-bold">Autogutachter24</h1>
                      <p className="text-blue-100 text-sm">Kfz-Sachverständigen Gutachten</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-blue-100">Gutachten-Nr.</p>
                    <p className="text-xl font-semibold">{fall.aktenzeichen}</p>
                    <p className="text-xs text-blue-100 mt-1">
                      Erstellt: {new Date(fall.created_at).toLocaleDateString('de-DE')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Gutachten Inhalt - Alle Sidebar-Punkte */}
              <div className="p-8 space-y-8">
                
                {/* Inhaltsverzeichnis */}
                <section className="bg-blue-50 p-6 rounded-lg border-2 border-blue-200">
                  <h3 className="text-xl font-bold text-blue-900 mb-4">Inhaltsverzeichnis</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <p>1. Akte & Auftrag</p>
                    <p>7. Lichtbilder</p>
                    <p>2. Beteiligte</p>
                    <p>8. Bewertung (Restwert & Minderwert)</p>
                    <p>3. Unterschrift</p>
                    <p>9. Beurteilung</p>
                    <p>4. Besichtigungen</p>
                    <p>10. Gutachten (Status)</p>
                    <p>5. Fahrzeug & Instandsetzung</p>
                    <p>11. Rechnung</p>
                    <p>6. Begutachtung</p>
                    <p>12. Dokumente</p>
                  </div>
                </section>
                {/* 1. Akte & Auftrag */}
                <section className="border-b pb-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                    <span className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm mr-3">1</span>
                    Akte & Auftrag
                  </h2>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <h3 className="font-semibold text-gray-700 mb-3">Kundendaten</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center space-x-2">
                          <User className="w-4 h-4 text-gray-400" />
                          <span className="font-medium">Kunde:</span>
                          <span>{fall.kunde || '-'}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Car className="w-4 h-4 text-gray-400" />
                          <span className="font-medium">Fahrzeugtyp:</span>
                          <span>{fall.fahrzeugtyp || '-'}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <MapPin className="w-4 h-4 text-gray-400" />
                          <span className="font-medium">Adresse:</span>
                          <span>{fall.adresse1 || '-'}, {fall.adresse2 || '-'}</span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-700 mb-3">Schadendaten</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center space-x-2">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <span className="font-medium">Schadentag:</span>
                          <span>{fall.schadentag ? new Date(fall.schadentag).toLocaleDateString('de-DE') : '-'}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <FileText className="w-4 h-4 text-gray-400" />
                          <span className="font-medium">Anfrageart:</span>
                          <span>{fall.anfrageart || '-'}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <MapPin className="w-4 h-4 text-gray-400" />
                          <span className="font-medium">Schadenort:</span>
                          <span>{fall.schadenort || '-'}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <FileText className="w-4 h-4 text-gray-400" />
                          <span className="font-medium">Schadennummer:</span>
                          <span>{fall.schadennummer || '-'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <h3 className="font-semibold text-gray-700 mb-3">Fahrzeugdaten</h3>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div className="bg-gray-50 p-3 rounded">
                        <p className="font-medium text-gray-600">Kennzeichen</p>
                        <p className="text-lg">{fall.kennzeichen || '-'}</p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded">
                        <p className="font-medium text-gray-600">Vers.-Nummer</p>
                        <p className="text-lg">{fall.vers_nummer || '-'}</p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded">
                        <p className="font-medium text-gray-600">Selbstbeteiligung</p>
                        <p className="text-lg">{fall.selbstbeteiligung || '0'} €</p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded">
                        <p className="font-medium text-gray-600">Marke</p>
                        <p className="text-lg">{fall.marke || '-'}</p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded">
                        <p className="font-medium text-gray-600">Modell</p>
                        <p className="text-lg">{fall.modell || '-'}</p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded">
                        <p className="font-medium text-gray-600">VIN</p>
                        <p className="text-sm">{fall.vin || '-'}</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6">
                    <h3 className="font-semibold text-gray-700 mb-3">Auftragsdaten</h3>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div className="bg-blue-50 p-3 rounded">
                        <p className="font-medium text-gray-600">Welche Scheibe</p>
                        <p className="text-lg">{fall.welche_scheibe || '-'}</p>
                      </div>
                      <div className="bg-blue-50 p-3 rounded">
                        <p className="font-medium text-gray-600">Auftragstyp</p>
                        <p className="text-lg">{fall.auftragstyp || '-'}</p>
                      </div>
                      <div className="bg-blue-50 p-3 rounded">
                        <p className="font-medium text-gray-600">Vorsteuerabzug</p>
                        <p className="text-lg">{fall.vorsteuerabzugsberechtigt || '-'}</p>
                      </div>
                    </div>
                  </div>
                </section>

                {/* 2. Beteiligte */}
                <section className="border-b pb-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                    <span className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm mr-3">2</span>
                    Beteiligte Personen
                  </h2>
                  <div className="bg-gray-50 p-6 rounded-lg text-center">
                    <svg className="w-16 h-16 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <p className="text-gray-500">Keine Beteiligten erfasst</p>
                    <p className="text-xs text-gray-400 mt-1">Daten werden in der Akte "Beteiligte" erfasst</p>
                  </div>
                </section>

                {/* 3. Unterschrift */}
                <section className="border-b pb-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                    <span className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm mr-3">3</span>
                    Abtretungserklärung & Unterschrift
                  </h2>
                  <div className="bg-blue-50 p-6 rounded-lg">
                    <h3 className="font-semibold text-blue-900 mb-3">Schadens- und Abtretungserklärung</h3>
                    <div className="bg-white p-4 rounded border border-blue-200 mb-4">
                      <p className="text-sm text-gray-700 leading-relaxed">
                        Hiermit trete ich meinen Schadenersatzanspruch / Leistungsanspruch gegen meine Kaskoversicherung 
                        bzw. gegen die Haftpflichtversicherung des Unfallgegners in Höhe der Reparaturkosten zur Sicherung 
                        des Anspruches auf Bezahlung der Reparaturkosten unwiderruflich an die oben genannte Werkstatt ab.
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <p className="font-medium text-gray-700 mb-2">Kunde / Versicherungsnehmer:</p>
                        <p className="text-lg">{fall.kunde || '-'}</p>
                      </div>
                      <div>
                        <p className="font-medium text-gray-700 mb-2">Unterschrift:</p>
                        <div className="border-2 border-dashed border-gray-300 rounded p-4 text-center text-gray-400">
                          Unterschrift nicht vorhanden
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* 4. Besichtigungen */}
                <section className="border-b pb-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                    <span className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm mr-3">4</span>
                    Besichtigungen
                  </h2>
                  <div className="bg-gray-50 p-6 rounded-lg text-center">
                    <svg className="w-16 h-16 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <p className="text-gray-500">Keine Besichtigungen erfasst</p>
                    <p className="text-xs text-gray-400 mt-1">Daten werden in der Akte "Besichtigungen" erfasst</p>
                  </div>
                </section>

                {/* 5. Fahrzeug & Instandsetzung */}
                <section className="border-b pb-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                    <span className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm mr-3">5</span>
                    Fahrzeug & Instandsetzung
                  </h2>
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="font-semibold text-gray-700 mb-4">Fahrzeugdetails</h3>
                    <div className="grid grid-cols-3 gap-4 text-sm mb-6">
                      <div>
                        <p className="font-medium text-gray-600">Marke</p>
                        <p className="text-lg">{fall.marke || '-'}</p>
                      </div>
                      <div>
                        <p className="font-medium text-gray-600">Modell</p>
                        <p className="text-lg">{fall.modell || '-'}</p>
                      </div>
                      <div>
                        <p className="font-medium text-gray-600">Typ</p>
                        <p className="text-lg">{fall.fahrzeugtyp || '-'}</p>
                      </div>
                    </div>
                    <div className="bg-white p-4 rounded border border-gray-200">
                      <p className="text-sm text-gray-500 text-center">
                        Detaillierte Instandsetzungsinformationen werden in der Akte erfasst
                      </p>
                    </div>
                  </div>
                </section>

                {/* 6. Begutachtung */}
                <section className="border-b pb-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                    <span className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm mr-3">6</span>
                    Begutachtung
                  </h2>
                  <div className="bg-gray-50 p-6 rounded-lg text-center">
                    <svg className="w-16 h-16 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                    <p className="text-gray-500">Begutachtung noch nicht durchgeführt</p>
                    <p className="text-xs text-gray-400 mt-1">Daten werden in der Akte "Begutachtung" erfasst</p>
                  </div>
                </section>

                {/* 7. Lichtbilder */}
                <section className="border-b pb-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                    <span className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm mr-3">7</span>
                    Lichtbilder
                  </h2>
                  {medien.length > 0 ? (
                    <>
                    <div className="grid grid-cols-4 gap-4">
                      {medien.slice(0, 8).map((media, index) => (
                        <div key={media.datei_id} className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden">
                          <img
                            src={media.url}
                            alt={`Bild ${index + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = '<div class="flex items-center justify-center w-full h-full"><svg class="w-12 h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg></div>';
                            }}
                          />
                          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 text-center">
                            Bild {index + 1}
                          </div>
                        </div>
                      ))}
                    </div>
                    {medien.length > 8 && (
                      <p className="text-sm text-gray-500 mt-2">+ {medien.length - 8} weitere Bilder</p>
                    )}
                    </>
                  ) : (
                    <div className="bg-gray-50 p-12 rounded-lg text-center">
                      <ImageIcon className="w-16 h-16 mx-auto text-gray-300 mb-3" />
                      <p className="text-gray-500">Keine Lichtbilder hochgeladen</p>
                      <p className="text-xs text-gray-400 mt-1">Bilder können über den "Medien"-Tab hochgeladen werden</p>
                    </div>
                  )}
                </section>

                {/* 8. Bewertung - Restwert & Minderwert */}
                <section className="border-b pb-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                    <span className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm mr-3">8</span>
                    Bewertung (Restwert & Minderwert)
                  </h2>
                  <div className="bg-blue-50 p-6 rounded-lg">
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <h3 className="font-semibold text-blue-900 mb-3">Restwert</h3>
                        <div className="bg-white p-4 rounded border border-blue-200 text-center">
                          <p className="text-gray-500">Noch nicht bewertet</p>
                        </div>
                      </div>
                      <div>
                        <h3 className="font-semibold text-blue-900 mb-3">Minderwert</h3>
                        <div className="bg-white p-4 rounded border border-blue-200 text-center">
                          <p className="text-gray-500">Noch nicht bewertet</p>
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-4 text-center">
                      Bewertungsdaten werden in der Akte "Bewertung" erfasst
                    </p>
                  </div>
                </section>

                {/* 9. Gutachterliche Beurteilung */}
                <section className="border-b pb-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                    <span className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm mr-3">9</span>
                    Gutachterliche Beurteilung
                  </h2>
                  <div className="space-y-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="font-semibold text-gray-700 mb-2">1. Schadenumfang und -beschreibung</h3>
                      <p className="text-sm text-gray-500 italic">Noch nicht erfasst</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="font-semibold text-gray-700 mb-2">2. Reparaturkosten und Kalkulation</h3>
                      <p className="text-sm text-gray-500 italic">Noch nicht erfasst</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="font-semibold text-gray-700 mb-2">3. Unfallhergang / Schadensursache</h3>
                      <p className="text-sm text-gray-500 italic">Noch nicht erfasst</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="font-semibold text-gray-700 mb-2">4. Haftung und Verschulden</h3>
                      <p className="text-sm text-gray-500 italic">Noch nicht erfasst</p>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-200">
                      <h3 className="font-semibold text-blue-900 mb-2">5. Zusammenfassung und Fazit</h3>
                      <p className="text-sm text-gray-500 italic">Noch nicht erfasst</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-4 text-center">
                    Beurteilungsdaten werden in der Akte "Bewertung > Beurteilung" erfasst
                  </p>
                </section>

                {/* 10. Gutachten-Status */}

                <section className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-lg border-2 border-blue-300">
                  <h2 className="text-2xl font-bold text-blue-900 mb-4 flex items-center">
                    <span className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm mr-3">10</span>
                    Gutachten-Status
                  </h2>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-700">Aktenzeichen:</span>
                      <span className="font-bold text-blue-900">{fall.aktenzeichen}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-700">Status:</span>
                      <Badge className={getStatusColor(fall.status)}>
                        {fall.status}
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-700">Erstellt am:</span>
                      <span>{new Date(fall.created_at).toLocaleDateString('de-DE')}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-700">Zuletzt aktualisiert:</span>
                      <span>{new Date(fall.updated_at).toLocaleDateString('de-DE')}</span>
                    </div>
                  </div>
                </section>

                {/* 11. Rechnung */}
                <section className="border-b pb-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                    <span className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm mr-3">11</span>
                    Rechnung
                  </h2>
                  <div className="bg-green-50 p-6 rounded-lg border-2 border-green-200">
                    <div className="text-center mb-6">
                      <svg className="w-16 h-16 mx-auto text-green-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2zM10 8.5a.5.5 0 11-1 0 .5.5 0 011 0zm5 5a.5.5 0 11-1 0 .5.5 0 011 0z" />
                      </svg>
                      <p className="text-gray-600">Rechnung noch nicht erstellt</p>
                    </div>
                    
                    <div className="bg-white p-4 rounded border border-green-300">
                      <h3 className="font-semibold text-gray-700 mb-3">Rechnungsübersicht</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Netto-Betrag:</span>
                          <span className="font-medium">-</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">MwSt. (19%):</span>
                          <span className="font-medium">-</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-gray-200">
                          <span className="font-semibold">Brutto-Betrag:</span>
                          <span className="font-bold text-green-700">-</span>
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-4 text-center">
                      Rechnungsdaten werden in der Akte "Rechnung" erfasst
                    </p>
                  </div>
                </section>

                {/* 12. Dokumente */}
                <section className="border-b pb-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                    <span className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm mr-3">12</span>
                    Dokumente
                  </h2>
                  {dokumente.length > 0 ? (
                    <div className="space-y-2">
                      {dokumente.map((dok) => (
                        <div key={dok.datei_id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg border border-gray-200">
                          <div className="flex items-center space-x-3">
                            <FileText className="w-5 h-5 text-blue-600" />
                            <div>
                              <p className="font-medium text-gray-900">{dok.dateiname}</p>
                              <p className="text-xs text-gray-500">
                                {new Date(dok.created_at).toLocaleDateString('de-DE')}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-gray-50 p-12 rounded-lg text-center">
                      <FileText className="w-16 h-16 mx-auto text-gray-300 mb-3" />
                      <p className="text-gray-500">Keine Dokumente hochgeladen</p>
                      <p className="text-xs text-gray-400 mt-1">Dokumente können über den "Dokumente"-Tab hochgeladen werden</p>
                    </div>
                  )}
                </section>

                {/* Footer */}
                <section className="pt-8 mt-8 border-t-2 border-gray-300">
                  <div className="flex justify-between items-start text-sm text-gray-600">
                    <div>
                      <p className="font-semibold text-gray-900">Autogutachter24</p>
                      <p>Kfz-Sachverständigenbüro</p>
                      <p>Gutachten erstellt am: {new Date().toLocaleDateString('de-DE')}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">Unterschrift Sachverständiger:</p>
                      <div className="mt-2 border-2 border-dashed border-gray-300 rounded p-4 text-gray-400 w-48">
                        _______________________
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-3 mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => window.print()}
                className="border-blue-600 text-blue-600 hover:bg-blue-50"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                Drucken
              </Button>
              <Button
                type="button"
                onClick={() => {
                  toast.success('PDF wird erstellt', {
        description: 'Das Gutachten wird als PDF generiert...'
      });
                }}
                className="bg-gradient-to-r from-blue-500 to-blue-700 text-white"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Als PDF exportieren
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default FallDetail;
